import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  XCircle, 
  Shield, 
  Trophy, 
  Users, 
  AlertTriangle,
  ArrowLeft
} from 'lucide-react';

const CommunityGuidelinesPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/wall">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Feed
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-10 w-10 text-primary" />
            <div>
              <h1 className="text-3xl font-heading font-bold">Community Guidelines</h1>
              <p className="text-muted-foreground">Keep it sports! 🏆</p>
            </div>
          </div>
        </div>

        {/* Welcome Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Welcome to RecruitLook Social!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              This is a <strong>sports-focused community</strong> built for athletes, coaches, parents, and fans. 
              Our goal is to create a positive, supportive environment where everyone can share their athletic 
              journey, connect with others, and stay focused on what matters most: sports.
            </p>
          </CardContent>
        </Card>

        {/* What's Welcome */}
        <Card className="mb-6 border-green-200 dark:border-green-900">
          <CardHeader className="bg-green-50 dark:bg-green-950/30">
            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle className="h-5 w-5" />
              What's Welcome Here
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <ul className="space-y-3">
              {[
                { emoji: '🏈', text: 'Sports games, scores, highlights, and plays' },
                { emoji: '💪', text: 'Training, workouts, practice sessions, and drills' },
                { emoji: '🎓', text: 'Recruiting updates, college visits, and commitments' },
                { emoji: '📰', text: 'Team news, trades, and draft discussions' },
                { emoji: '🧠', text: 'Athlete mental health and recovery (in sports context)' },
                { emoji: '🎯', text: 'Sports motivation, mindset, and goals' },
                { emoji: '⚙️', text: 'Equipment, gear, and nutrition for athletes' },
                { emoji: '🏕️', text: 'Sports events, camps, and showcases' },
                { emoji: '🤝', text: 'Teammate shoutouts and team bonding' },
                { emoji: '👨‍🏫', text: 'Coach and athlete relationships' },
                { emoji: '📊', text: 'Sports statistics and analysis' },
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-lg">{item.emoji}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* What's Not Allowed */}
        <Card className="mb-6 border-red-200 dark:border-red-900">
          <CardHeader className="bg-red-50 dark:bg-red-950/30">
            <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <XCircle className="h-5 w-5" />
              What's Not Allowed
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <ul className="space-y-3">
              {[
                { emoji: '🚫', text: 'Political content (elections, politicians, policy debates, voting)' },
                { emoji: '🚫', text: 'Religious debates or proselytizing' },
                { emoji: '🚫', text: 'Controversial social/cultural issues unrelated to sports' },
                { emoji: '🚫', text: 'Hate speech, harassment, or bullying' },
                { emoji: '🚫', text: 'Profanity or vulgar language' },
                { emoji: '🚫', text: 'Sexual content or innuendo' },
                { emoji: '🚫', text: 'Violence or threats' },
                { emoji: '🚫', text: 'Spam, promotions, or ads unrelated to sports' },
                { emoji: '🚫', text: 'Cryptocurrency, NFT, or financial promotions' },
                { emoji: '🚫', text: 'Off-topic content (food blogs, travel, entertainment gossip)' },
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-muted-foreground">
                  <span className="text-lg">{item.emoji}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Consequences */}
        <Card className="mb-6 border-amber-200 dark:border-amber-900">
          <CardHeader className="bg-amber-50 dark:bg-amber-950/30">
            <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              Enforcement
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-muted-foreground mb-4">
              We use a combination of AI moderation and human review to keep our community safe. 
              Violations may result in:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-yellow-500" />
                <strong>Strike 1:</strong> Warning notification
              </li>
              <li className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-orange-500" />
                <strong>Strike 2:</strong> 24-hour posting restriction
              </li>
              <li className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                <strong>Strike 3:</strong> 7-day posting restriction
              </li>
              <li className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-red-700" />
                <strong>Strike 4+:</strong> Admin review, possible permanent restriction
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Final Message */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6 text-center">
            <Trophy className="h-12 w-12 mx-auto text-primary mb-4" />
            <h3 className="text-xl font-semibold mb-2">Let's Keep It About Sports!</h3>
            <p className="text-muted-foreground mb-4">
              Together, we can build the best community for athletes and sports enthusiasts. 
              Thank you for being part of RecruitLook!
            </p>
            <Link to="/wall">
              <Button>
                Return to Feed
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CommunityGuidelinesPage;
